import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";
import { initializeSocket } from "../../utils/socket";
import { useNotifications } from "../../context/NotificationContext";

const UserNotificationHandler = ({ userId }) => {
  const context = useNotifications();
  const navigate = useNavigate();
  
  // Safety: Extract with fallbacks to avoid "not a function" errors
  const setNotifications = context?.setNotifications;
  const setUnreadCount = context?.setUnreadCount;
  const setIsLoading = context?.setIsLoading;

  const token = localStorage.getItem("userToken") || localStorage.getItem("token");

  const fetchNotifications = useCallback(async () => {
    if (!token || !setIsLoading) {
      if (typeof setIsLoading === "function") setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await axiosInstance.get("/users/notifications", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (setNotifications) setNotifications(res.data.notifications || []);
      if (setUnreadCount) setUnreadCount(res.data.unreadCount || 0); 
    } catch (err) {
      console.error("Notification API Error:", err.response || err);
    } finally {
      if (typeof setIsLoading === "function") setIsLoading(false);
    }
  }, [token, setIsLoading, setNotifications, setUnreadCount]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!token || !userId || !setNotifications || !setUnreadCount) return;

    const socket = initializeSocket(token);
    if (!socket) return;

    const joinRoom = () => socket.emit("join-notification", { userId, role: "user" });
    joinRoom();
    // Re-join on every (re)connect — socket.io does not remember rooms
    // across a reconnect, so without this, a dropped connection (server
    // restart, network blip, laptop sleep) silently stops delivering
    // real-time updates until the page is refreshed.
    socket.on("connect", joinRoom);

    const handler = (data) => {
      if (!data) return;
      if (data.recipientType && data.recipientType !== "user") return;
      if (data.recipientId && userId && data.recipientId.toString() !== userId.toString()) return;

      setNotifications((prev) => [data, ...prev]);
      setUnreadCount((c) => c + 1);

      if (data.type === "HELPDESK_RESPONSE" || data.type === "HELPDESK_QUERY") {
        window.dispatchEvent(new Event("helpdesk_updated"));
      }

      toast.info(
        <div onClick={() => data.actionUrl && navigate(data.actionUrl)}>
          <strong>{data.title}</strong>
          <div style={{ fontSize: "0.85em" }}>{data.message}</div>
        </div>,
        { autoClose: 8000 }
      );
    };

    const handleHelpdeskSocket = () => {
      window.dispatchEvent(new Event("helpdesk_updated"));
    };

    socket.on("notification", handler);
    socket.on("helpdesk_updated", handleHelpdeskSocket);

    return () => {
      socket.off("connect", joinRoom);
      socket.off("notification", handler);
      socket.off("helpdesk_updated", handleHelpdeskSocket);
    };
  }, [token, userId, setNotifications, setUnreadCount, navigate]);

  return null;
};

export default UserNotificationHandler;